import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import first from 'lodash/fp/first';
import get from 'lodash/fp/get';
import isFinite from 'lodash/isFinite';

import ColorSwatch from 'common/components/shop/ColorSwatch';
import ProductImage from 'common/components/shop/ProductImage';
import RadioButtonGroup from 'common/components/Form/RadioButtonGroup';
import Button from 'common/components/Button';
import PriceTag from 'common/components/shop/PriceTag';
import PriceSpan from 'common/components/formatters/PriceSpan';
import { TranslatedMessage } from 'common/components/Intl';
import {
  getOfferingPrice,
  sortProductOfferingsByPrice,
} from '../../../apps/product/util/offerings';

function ProductDisplayPriceTag({ product }) {
  const sortedOfferings = sortProductOfferingsByPrice(product);

  const bestOffering = first(sortedOfferings);
  const price = getOfferingPrice(product, bestOffering);

  if (!isFinite(price)) return null;

  const priceSpan = <PriceSpan amount={price} />;

  // If there are multiple offerings with a price, we want to display "From <lowest price>"
  const priceComponent =
    sortedOfferings.length > 1 ? (
      <TranslatedMessage
        id="page.shop.product_display.from_price"
        values={{ price: priceSpan }}
      />
    ) : (
      priceSpan
    );

  return (
    <PriceTag
      autoShip={!!get('commitmentReward')(bestOffering)}
      price={priceComponent}
    />
  );
}

function ProductDisplay({
  renderCallToActionButtons,
  heading,
  imagePosition,
  learnMoreText,
  mobileLayout,
  lede,
  products,
}) {
  const [selectedProduct, setSelectedProduct] = useState(products[0]);

  const showProductSelector = products.length > 1;
  const containerClassNames = classNames('product-display__container', {
    'product-display__container--mobile-layout': mobileLayout,
    'product-display__container--image-start': imagePosition !== 'end',
    'product-display__container--image-end': imagePosition === 'end',
  });
  const radioButtonGroupName =
    showProductSelector && uniqueId('color-swatches-');

  return (
    <div className={containerClassNames}>
      <div className="product-display__heading-container">
        <h2 className="product-display__heading subheading--small">
          {heading}
        </h2>

        <h3 className="product-display__lede natural-heading">{lede}</h3>
      </div>

      <div className="product-display__image">
        <ProductImage
          src={selectedProduct.src}
          detail={<ProductDisplayPriceTag product={selectedProduct} />}
          starRating={selectedProduct.starRating}
        />
      </div>

      <div className="product-display__cta-container">
        {showProductSelector && (
          <RadioButtonGroup
            className="product-display__color-swatch-selector"
            defaultValue={products[0]}
            inline
            name={radioButtonGroupName}
            render={({ selectedValue, setSelectedValue }) =>
              products.map(product => (
                <ColorSwatch
                  color={product.colorValue}
                  isSelected={selectedValue === product}
                  key={product.title}
                  name={radioButtonGroupName}
                  onChange={() => {
                    setSelectedValue(product);
                    setSelectedProduct(product);
                  }}
                  value={product.colorName || product.title}
                />
              ))
            }
          />
        )}

        <div className="product-display__cta-buttons">
          {renderCallToActionButtons(selectedProduct)}
          <Button
            className="product-display__learn-more button--tertiary"
            href={selectedProduct.url}
          >
            {learnMoreText}
          </Button>
        </div>
      </div>
    </div>
  );
}

export const productDisplayProductPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    colorValue: PropTypes.string,
    colorName: PropTypes.string,
    offerings: PropTypes.arrayOf(PropTypes.object),
    price: PropTypes.number,
    src: PropTypes.string.isRequired,
    starRating: PropTypes.number,
    title: PropTypes.string.isRequired,
    url: PropTypes.string,
  })
);

export const productDisplayPropTypes = {
  heading: PropTypes.string,
  imagePosition: PropTypes.oneOf(['start', 'end']),
  learnMoreText: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
    .isRequired,
  lede: PropTypes.string,
  mobileLayout: PropTypes.bool,
  products: productDisplayProductPropTypes.isRequired,
  renderCallToActionButtons: PropTypes.func,
};

ProductDisplay.propTypes = productDisplayPropTypes;

ProductDisplay.defaultProps = {
  mobileLayout: false,
  onLearnMoreClick: () => {}, // eslint-disable-line no-empty-function
  renderCallToActionButtons: () => {}, // eslint-disable-line no-empty-function
};

export default ProductDisplay;
